import { IResponseData, IColumnList, TFieldMeta } from '../../types/spreadsheet-types';
import { getIdFromUrl } from '../../utils/getIdFromUrl';
import getPageFieldIsOnFromTokens from '../../utils/getPageFieldIsOnFromTokens';
import {
  IValidationErrorByExtraction,
  IValidationErrorByExtractionDetails,
} from '../../types/validation-types';
import {
  extractionFieldHasIgnoredErrors,
  getValidationErrorsForSingleField,
  getHighestPriorityValidationError,
  getValidationMessages,
} from '../../utils/validationUtils';

/**
 * Determines the validation status based on the highest priority validation error.
 * @param highestPriorityValidationError - The highest priority validation error, or undefined if none exists
 * @returns 'required' if the error type is 'required', 'error' for other error types, or null if no error
 */
const getValidationStatus = (
  highestPriorityValidationError: IValidationErrorByExtractionDetails | undefined
): 'required' | 'error' | null => {
  if (!highestPriorityValidationError?.type) {
    return null;
  }
  if (highestPriorityValidationError.type === 'required') {
    return 'required';
  }
  return 'error';
};

export const confRows = (
  retData: IResponseData[],
  validationSummaryByExtractionId: IValidationErrorByExtraction[],
  validatingExtractionIds: ReadonlySet<string> = new Set(),
  validationNotReadyExtractionIds: ReadonlySet<string> = new Set()
) => {
  // FIXME: Types
  let rows: any = [];
  for (let docIndex = 0; docIndex < retData.length; docIndex++) {
    const extraction: IResponseData = retData[docIndex];
    let addFields: IColumnList = {};

    // store meta data on a field - for now, just the page number
    let fieldMeta: Record<string, TFieldMeta> = {};

    // Add document ID
    const documentId = getIdFromUrl(extraction?.data?.document);
    const extractionId = extraction?.data?.id;

    addFields['documentId'] = documentId || '';

    addFields['transformationStatus'] = extraction?.data?.data_processing_status || '';

    // build the row object
    extraction?.data?.results &&
      Object.keys(extraction.data.results).forEach(function (fieldsKey) {
        addFields[fieldsKey] = extraction.data.results[fieldsKey].content;
      });

    // Look through the validationSummaryByExtractionId in context to see if the extraction
    // we are viewing has any validation errors
    const validationErrorsForThisExtraction = validationSummaryByExtractionId?.find(
      (v: IValidationErrorByExtraction) => v.extractionId === extractionId
    );

    // While the extraction's validation is being refetched, or its poll budget ran out,
    // the row shows that state rather than the last validation result.
    const validating = validatingExtractionIds.has(extractionId);
    const validationNotReady = !validating && validationNotReadyExtractionIds.has(extractionId);
    const hasStaleValidationResult = validating || validationNotReady;

    // Build the field meta - [field] => pageNumber
    extraction?.data?.results &&
      Object.keys(extraction.data.results).forEach(function (fieldsKey) {
        const thisFieldHasIgnoredError = validationErrorsForThisExtraction
          ? extractionFieldHasIgnoredErrors(
              validationErrorsForThisExtraction.validationErrorDetails,
              fieldsKey
            )
          : false;

        // Get all validation errors for this field
        const validationErrorsForThisField = getValidationErrorsForSingleField(
          validationErrorsForThisExtraction,
          fieldsKey
        );

        // Get the highest priority one
        const highestPriorityValidationError = getHighestPriorityValidationError(
          validationErrorsForThisField
        );

        // Find the first available dismissal reason for this field (if any)
        const dismissalReasonForThisField =
          validationErrorsForThisField.find((v) => v.dismissalReason)?.dismissalReason ?? null;

        // Retrieve a list of all validation errors (for tooltip)
        const allValidationErrorsMessages: string[] = getValidationMessages(
          validationErrorsForThisField,
          extraction.data.results[fieldsKey].content
        );

        // Build a fieldMeta object for each field. This will include the page number the field is
        // currently on, that validation status (to set the color of the cell via a class), the tooltip,
        // and whether the field is ignored. This allows us to quickly access this information
        // when rendering the cell.
        fieldMeta[fieldsKey] = {
          pageNumber: getPageFieldIsOnFromTokens(extraction.data.results[fieldsKey].tokens),
          validationStatus: hasStaleValidationResult
            ? null
            : getValidationStatus(highestPriorityValidationError),
          tooltip: hasStaleValidationResult ? '' : allValidationErrorsMessages.join(', ') || '',
          ignored: hasStaleValidationResult ? false : thisFieldHasIgnoredError,
          dismissalReason: hasStaleValidationResult ? null : dismissalReasonForThisField,
        };
      });

    rows.push({
      documentNumber: docIndex + 1,
      extractionId: extraction?.data?.id,
      ...addFields,
      fieldMeta,
      validationResponse: hasStaleValidationResult ? undefined : validationErrorsForThisExtraction,
      validating,
      validationNotReady,
    });
  }

  return rows;
};
